// Romania "locations" exactly as listed (and ordered) in Adeline's PDF.
// Each location groups the hotels & stays from that document, carries a
// North / Central / South zone used for the destination tabs, and a photo
// set used for the auto-scrolling card carousels.
//
// Single source of truth: hotel names/images come from hotels.romania.ts and
// scenic shots from destinations.romania.ts — nothing is re-declared here.

import { romaniaHotels, romaniaRegions, type RomaniaHotel } from './hotels.romania';
import { destinationsRomania } from './destinations.romania';

export type RomaniaZone = 'North' | 'Central' | 'South';

/** Tabs shown above the Romanian Destinations grid (beaches tab removed). */
export const romaniaZoneTabs = ['All', 'North', 'Central', 'South'] as const;
export type RomaniaZoneTab = (typeof romaniaZoneTabs)[number];

export interface LocationPhoto {
  src: string;
  alt: string;
}

export interface RomaniaLocation {
  id: string;
  /** Location name exactly as in the PDF (matches hotel `region`). */
  name: string;
  zone: RomaniaZone;
  tagline: string;
  description: string;
  /** Carousel photo set for the location card. */
  images: LocationPhoto[];
  /** Hotels & stays for this location, straight from the PDF grouping. */
  hotels: RomaniaHotel[];
}

// ── helpers: pull photos from the existing data modules ──────────────
const hotelById = (id: number): RomaniaHotel =>
  romaniaHotels.find((h) => h.id === id)!;

const hotelPhoto = (id: number): LocationPhoto => {
  const h = hotelById(id);
  return { src: h.image, alt: h.alt };
};

const destPhoto = (id: number): LocationPhoto => {
  const d = destinationsRomania.find((x) => x.id === id)!;
  return { src: d.image, alt: d.alt || `${d.name}, ${d.location}, Romania` };
};

// ── per-location config (keyed by the PDF location name) ─────────────
const locationConfig: Record<
  string,
  { zone: RomaniaZone; tagline: string; description: string; images: LocationPhoto[] }
> = {
  Bucharest: {
    zone: 'South',
    tagline: 'The Capital',
    description:
      'Romania\'s vibrant capital blends Belle Époque grandeur with a buzzing café culture — palace hotels, the Old Town and the colossal Palace of the Parliament.',
    images: [destPhoto(5), hotelPhoto(101), hotelPhoto(102), hotelPhoto(103)],
  },
  'Danube Delta': {
    zone: 'South',
    tagline: 'Wild Waterways',
    description:
      'Europe\'s largest wetland and a UNESCO Biosphere Reserve — a labyrinth of channels and reed beds teeming with pelicans, explored from serene waterfront resorts.',
    images: [destPhoto(7), hotelPhoto(201), hotelPhoto(202)],
  },
  'Brașov': {
    zone: 'Central',
    tagline: 'Mountain Gateway',
    description:
      'A Saxon gem framed by the Carpathians — medieval squares, Bran Castle and Poiana Brașov\'s slopes, with chalets and grand mountain hotels at every turn.',
    images: [destPhoto(8), hotelPhoto(301), hotelPhoto(306), hotelPhoto(304)],
  },
  'Carpathians & Transylvania': {
    zone: 'Central',
    tagline: 'Estates & Wilderness',
    description:
      'Aristocratic estates, castle stays and off-grid mountain retreats deep in one of Europe\'s last great wildernesses — home to bears, wolves and timeless villages.',
    images: [destPhoto(10), hotelPhoto(401), hotelPhoto(403), hotelPhoto(407)],
  },
  'Sighișoara & Viscri': {
    zone: 'Central',
    tagline: 'Saxon Heritage',
    description:
      'A perfectly preserved UNESCO citadel and the storybook village of Viscri — restored Saxon farmsteads, fortified churches and slow-food country living.',
    images: [destPhoto(4), hotelPhoto(501), hotelPhoto(502)],
  },
  'Maramureș': {
    zone: 'North',
    tagline: 'Living Traditions',
    description:
      'Carved wooden gates, hay meadows and ancient wooden churches — village pensions and countryside resorts keep Romania\'s most authentic rural life alive.',
    images: [hotelPhoto(601), hotelPhoto(602), hotelPhoto(603)],
  },
  Bucovina: {
    zone: 'North',
    tagline: 'Painted Monasteries',
    description:
      'UNESCO monasteries glowing in Voroneț blue among rolling northern hills — organic farm-stays, country estates and alpine chalets complete the escape.',
    images: [destPhoto(6), hotelPhoto(706), hotelPhoto(702), hotelPhoto(707)],
  },
};

/** Locations in the exact order they appear in Adeline's PDF. */
export const romaniaLocations: RomaniaLocation[] = romaniaRegions.map((name) => {
  const cfg = locationConfig[name];
  return {
    id: name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
    name,
    zone: cfg.zone,
    tagline: cfg.tagline,
    description: cfg.description,
    images: cfg.images,
    hotels: romaniaHotels.filter((h) => h.region === name),
  };
});
